import React, { Component } from 'react'
import trans from "trans"
import { connect } from "react-redux"
import _find from "lodash/find"

import {
    css,
    Grid,
    GridCell,
    Button,
    Theme,
    Spinner,
    Icon,
} from "@bdfx/mergado-ui-kit"

import { URL } from "app/config/api-config";
import { getGradients, projectId, saveInvertMaskObjects } from "app/helpers/api";
import Mergado from "app/helpers/mergado"
import { isJson } from "app/helpers/other";

import { PreviewImage, PreviewsData, PreviewsForm } from 'template-previews/types';
import uniqueId from "app/helpers/unique_id";
import { LEFT_ARROW_KEY, RIGHT_ARROW_KEY } from "app/helpers/keymap";
import WatermarksMarker from './watermarks-marker';
import { Gradient, SettingsFormType, Template } from 'app/types/model-types'
import { ReduxList } from 'app/types/app-types';
import { ObjectsType } from './watermark-marker-common'
import InvertMaskMarker, { InvertMaskModeType } from './invert-mask-marker';
import { AxiosResponse } from 'axios'
import { addClass, hasClass, removeClass } from 'shape-edit/forms/helpers'
import { selectFormValues } from 'app/reducers/form'
import { setGradientsToStore } from 'template-edit/reducers'
import { RENDERED_TYPE_AUTOWMREMOVE, RENDERED_TYPE_BGREMOVE, RENDERED_TYPE_MANUAL_WMREMOVE } from 'products/types'
import { debounce } from 'lodash'

interface Props {
    previewsForm: PreviewsForm
    settingsForm: SettingsFormType
    previews: PreviewsData
    templates: ReduxList<Template>
    isTheLast: boolean
    isTheFirst: boolean
    idx: number
    showUnmodified: boolean
    handlePaginator: Function
    close: Function
    updateUserSetObjects: Function
    gradients: Gradient[]
    setGradientsToStore: typeof setGradientsToStore
}

interface State {
    isLoaded: boolean
    idx: number
    previewItemsInWrapper: PreviewImage[]
}

const MERGADO_APP = 'https://app.mergado.com/projects/' + projectId + '/product'


class ImagePreviewModalWrapper extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            isLoaded: false,
            idx: props.idx,
            previewItemsInWrapper: [...props.previews.items],
        }
    }

    updateGradients = () => {
        getGradients()
            .then((response: AxiosResponse<Gradient[]>) => {
                this.props.setGradientsToStore(response.data)
            })
            .catch(error => {
                console.error('cannot set gradients', error)
            })
    }

    componentDidMount(): void {
        document.addEventListener('keydown', this.handleKeyDown)
        document.addEventListener('click', this.handleClickEvent)
        this.updateModalClass()
        this.updateGradients()
    }

    componentWillUnmount(): void {
        document.removeEventListener('click', this.handleClickEvent)
        document.removeEventListener('keydown', this.handleKeyDown)
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if ((prevProps && (
            this.props.idx && prevProps.idx !== this.props.idx || prevProps.previews.page !== this.props.previews.page ||
            JSON.stringify(prevProps.previews.items) !== JSON.stringify(this.props.previews.items)
        ))) {
            this.setState({ isLoaded: false, previewItemsInWrapper: [...this.props.previews.items] })
        } else if ((prevState && this.state.idx && prevState.idx !== this.state.idx)) {
            this.updateModalClass()
        }
    }

    updateModalClass = () => {
        const elements = document.getElementsByClassName('react-confirm-alert-overlay')
        const el = elements[0] ?? null
        const previewImage = this.state.previewItemsInWrapper[this.state.idx] ?? null
        if (el && previewImage) {
            const showInvertMaskMarker = previewImage.renderedType === RENDERED_TYPE_BGREMOVE && previewImage.isSourceByTypeExists
            const showWatermarkMarker = [RENDERED_TYPE_AUTOWMREMOVE, RENDERED_TYPE_MANUAL_WMREMOVE].includes(previewImage.renderedType) && previewImage.isSourceByTypeExists

            const hasBgRemoveModalClass = hasClass(el, 'bgremove-modal')
            const hasWmRemoveModalClass = hasClass(el, 'wmremove-modal')

            if (showInvertMaskMarker && !hasBgRemoveModalClass) {
                addClass(el, 'bgremove-modal')
            } else if (!showInvertMaskMarker && hasBgRemoveModalClass) {
                removeClass(el, 'bgremove-modal')
            }

            if (showWatermarkMarker && !hasWmRemoveModalClass) {
                addClass(el, 'wmremove-modal')
            } else if (!showWatermarkMarker && hasWmRemoveModalClass) {
                removeClass(el, 'wmremove-modal')
            }
        }
    }

    handleKeyDown = (event) => {
        const { isTheFirst, isTheLast } = this.props

        const previewImage = this.state.previewItemsInWrapper[this.state.idx] ?? null
        const showInvertMaskMarker = previewImage.renderedType === RENDERED_TYPE_BGREMOVE && previewImage.isSourceByTypeExists

        if (!showInvertMaskMarker) {
            if (event.keyCode === RIGHT_ARROW_KEY) {  //right
                event.preventDefault()
                if (!isTheLast) {
                    this.nextProduct()
                }
            } else if (event.keyCode === LEFT_ARROW_KEY) {   //left
                event.preventDefault()
                if (!isTheFirst) {
                    this.prevProduct()
                }
            }
        }
    }

    updatePreviewItemInWrapper = (idx: number, previewItem: PreviewImage): void => {
        const { previewItemsInWrapper } = this.state
        let newUpdatedPreviewItems = [...previewItemsInWrapper]
        newUpdatedPreviewItems[idx] = previewItem
        this.setState({ previewItemsInWrapper: newUpdatedPreviewItems })
    }

    handleClickEvent = (event) => {
        const { isTheLast } = this.props

        //move to the next product
        if (event.target && event.target.type && event.target.type === 'radio') {
            if (!isTheLast) {
                this.nextProduct()
            }
        }
    }

    updateImageInfo = debounce((el, id) => {
        const { isLoaded } = this.state
        const wrapper = document.getElementById(`image-info-wrapper${id}`)
        if (el && el.target && wrapper && isLoaded) {
            wrapper.innerHTML = `${el.target.naturalWidth}x${el.target.naturalHeight}`
        } else if (wrapper) {
            wrapper.innerHTML = ''
        }
    }, 250)

    errorImageInfo = (id) => {
        const wrapper = document.getElementById(`image-info-wrapper${id}`)
        if (wrapper) {
            wrapper.innerHTML = trans('error_loading_image')
        }
    }

    nextProduct = () => {
        const { previews, previewsForm } = this.props
        const productsPerPage = previewsForm.view.itemsPerPage
        const currentPage = (previewsForm.view.page ?? 1)

        let newIndex = (this.state.idx + 1) % productsPerPage

        if (newIndex === 0) {   //next page
            if (currentPage < previews.last_page) {
                this.props.handlePaginator(currentPage + 1)
                newIndex = 0
            } else {
                this.props.close()
            }
        }

        this.setState({ idx: newIndex })
    }

    prevProduct = () => {
        const { previews, previewsForm, isTheFirst } = this.props
        const productsPerPage = previewsForm.view.itemsPerPage
        const currentPage = (previewsForm.view.page ?? 1)

        let newIndex = (this.state.idx - 1)

        if (newIndex < 0) {   //prev page
            if (previews.page > 1) {
                this.props.handlePaginator(currentPage - 1)
                newIndex = 0
            } else {
                this.props.close()
            }
        }

        const updatedIdx = newIndex >= 0 ? newIndex : (productsPerPage - 1)
        this.setState({ idx: updatedIdx })
    }


    render() {
        const { isTheFirst, isTheLast, showUnmodified, previews, templates, previewsForm, gradients } = this.props
        const { idx, previewItemsInWrapper } = this.state

        const previewImage = previewItemsInWrapper[idx] ?? null

        if (!previewImage) {
            return <div><Spinner type={'bubbles'} /></div>
        }

        const itemId = previewImage.itemId || ''
        const productId = previewImage.id
        const templateId = previewImage.templateId
        const productUrl = previewImage.shopUrl
        const mergadoProductUrl = `${MERGADO_APP}/${previewImage.id}`
        const previewUrl = `${URL.laravelBase}images?template_id=${templateId}&product_id=${productId}`
        const imageUrl = showUnmodified ? (previewImage.imageUrl || '') : previewUrl
        const item_id = itemId && isJson(itemId) ? JSON.parse(itemId) : null
        const firstItemId = item_id && item_id[0] ? item_id[0].item_id : ''

        const prevNextIconSize = 28
        const linksIconSize = 20

        const template = templateId ? _find(templates.data, (t: Template) => {
            return `${t.id}` === `${templateId}`
        }) : null


        const templateGradient = template?.gradient_id && gradients ? _find(gradients, (g: Gradient) => {
            return `${g.id}` === `${template.gradient_id}`
        }) : null

        //manual edit for some template types:
        const isRemoveWatermark = template?.type === RENDERED_TYPE_AUTOWMREMOVE && previewImage.imageHash && previewImage.imageWidth && previewImage.imageHeight
        const isRemoveBackground = template?.type === RENDERED_TYPE_BGREMOVE && previewImage.imageHash && previewImage.imageWidth && previewImage.imageHeight

        /* const noCache = previewsForm.view.allowCache === 1 ? 0 : 1 */

        return (
            <StyledModalGrid cols={'60px auto 60px'}>
                <GridCell className={'prev-link'}>
                    {!isTheFirst &&
                        <Button
                            type={'void'}
                            secondary={true}
                            color={'nocolor'}
                            onClick={() => this.prevProduct()}>
                            {<Icon type={idx < 1 ? 'chevrons-left-light' : 'chevron-left'} size={prevNextIconSize} color={Theme.blue} />}
                        </Button>}
                </GridCell>

                <GridCell style={{ alignSelf: 'center' }}>
                    {isRemoveWatermark ?
                        <WatermarksMarker
                            productId={productId}
                            isAlt={false}
                            imageOrAltImageId={previewImage.imageId}
                            imageOrAltImageHash={previewImage.imageHash}
                            imageOrAltImageObjects={previewImage.userSetObjects}
                            imageOrAltImageUrl={previewImage.imageUrl}
                            imageOrAltImageUrlHash={previewImage.imageUrlHash}
                            imageOrAltImageWidth={previewImage.imageWidth}
                            imageOrAltImageHeight={previewImage.imageHeight}
                            imageOrAltImageMaskHash={previewImage.maskHash}
                            updateWatermarkMasks={(objects: ObjectsType) => {
                                this.props.updateUserSetObjects(idx, objects, null)
                                this.updatePreviewItemInWrapper(idx, {
                                    ...previewItemsInWrapper[idx],
                                    userSetObjects: JSON.stringify(objects),
                                })
                            }}
                            setByUser={null}
                            changeSetByUser={(value) => { console.debug('change set by user', value, productId, previewImage.imageId) }}
                            closeModal={this.props.close}
                            idx={idx}
                            altLevel={null}
                            showClose={false}
                        /> : isRemoveBackground && previewImage.isSourceByTypeExists ?
                            <InvertMaskMarker
                                product_id={productId}
                                image_id={previewImage.imageId}
                                form={
                                    {
                                        ...template,
                                        gradient: templateGradient
                                    }
                                }
                                isAlt={false}
                                image_hash={previewImage.imageHash}
                                image_url={previewImage.imageUrl}
                                image_url_hash={previewImage.imageUrlHash}
                                image_width={previewImage.imageWidth}
                                image_height={previewImage.imageHeight}
                                invert_user_set_objects={previewImage.userSetObjects}
                                invert_mask_hash={previewImage.maskHash}
                                invert_mask_mode={previewImage.maskMode}
                                saveObjects={(objects: ObjectsType, mode: InvertMaskModeType) => {
                                    saveInvertMaskObjects(previewImage.imageId, previewImage.imageHash, objects, mode, previewImage.imageWidth, previewImage.imageHeight, false)
                                        .then((response: AxiosResponse<any>) => {
                                            const maskHash = response.data?.maskHash
                                            const maskMode = response.data?.maskMode
                                            const userSetObjects = response.data?.userSetObjects
                                            this.props.updateUserSetObjects(idx, objects, mode)
                                            this.updatePreviewItemInWrapper(idx, {
                                                ...previewItemsInWrapper[idx],
                                                maskHash: maskHash,
                                                maskMode: maskMode,
                                                userSetObjects: userSetObjects,
                                            })
                                        })
                                        .catch((e) => {
                                            console.error('cannot save invert mask', e)
                                        })
                                }}
                                closeModal={this.props.close}
                                idx={idx}
                                settingsForm={this.props.settingsForm}
                            />
                            : <Grid cols={'auto'} className={`image-div big-div`}>
                                <GridCell style={{ alignSelf: 'center', justifyContent: "stretch" }}>
                                    <div style={{ position: "relative", bottom: '10px', fontSize: '10px', color: '#000' }}>

                                        {!this.state.isLoaded && !isRemoveWatermark &&
                                            <div><Spinner type={'bubbles'} /></div>
                                        }

                                        {previewImage.imageUrl ?
                                            <React.Fragment>
                                                {!isRemoveWatermark &&
                                                    <div className={this.state.isLoaded ? 'visible' : 'hidden'}>
                                                        <img src={imageUrl} onLoad={() => this.setState({ isLoaded: true })} />
                                                    </div>
                                                }
                                            </React.Fragment>
                                            :
                                            <div className={`no-preview-image`} key={`${uniqueId()}`}>
                                                <Icon type={'image'} size={15} text={trans("no_image") + ` ${previewImage.id}`} /><br />
                                                <span className={'shop_url'}>{previewImage.shopUrl}</span>
                                            </div>
                                        }
                                    </div>
                                </GridCell>
                            </Grid>
                    }
                </GridCell>

                <GridCell className={'next-link'}>
                    {!isTheLast &&
                        <Button
                            secondary={true}
                            type={'void'}
                            color={'nocolor'}
                            onClick={() => this.nextProduct()}
                            icon={<Icon type={idx > 8 ? 'chevrons-right-light' : 'chevron-right'}
                                size={prevNextIconSize}
                                color={Theme.blue} />}
                        />
                    }
                </GridCell>

                {!isRemoveWatermark && !isRemoveBackground &&
                    <GridCell col={'1/-1'}>
                        <StyledMiddleColumnGrid cols={`repeat(5, 1fr)`}>
                            {previewImage.imageUrl &&
                                <GridCell className='clickable' onClick={() => Mergado.openWindow(previewImage.imageUrl)} title={trans("link_image_url")}>
                                    <img
                                        className={'main-image'}
                                        src={previewImage.imageUrl}
                                        style={{ maxWidth: '60px', maxHeight: '60px' }}
                                        onLoad={(e) => this.updateImageInfo(e, previewImage.id)}
                                        onError={() => this.errorImageInfo(previewImage.id)}
                                    />
                                    <div id={`image-info-wrapper${previewImage.id}`}>&nbsp;</div>
                                </GridCell>
                            }
                            {previewUrl &&
                                <GridCell className='clickable'>
                                    <div onClick={() => Mergado.openWindow(previewUrl)}>
                                        <Icon type='feedimageeditor' title={trans("link_fie")} size={linksIconSize} />
                                    </div>
                                </GridCell>
                            }
                            {mergadoProductUrl &&
                                <GridCell className='clickable'>
                                    <div onClick={() => Mergado.openWindow(mergadoProductUrl)}>
                                        <Icon type='mergado' title={trans("link_mergado")} size={linksIconSize} />
                                    </div>
                                </GridCell>
                            }
                            {productUrl &&
                                <GridCell className='clickable'>
                                    <div onClick={() => Mergado.openWindow(productUrl)}>
                                        <Icon type="shopping-basket" title={trans("link_eshop")} size={linksIconSize} />
                                    </div>
                                </GridCell>
                            }
                            <GridCell className='item-id' style={{ whiteSpace: 'nowrap' }}>
                                <Icon type='tag' text={firstItemId || itemId} title={"ITEM_ID"} />
                            </GridCell>
                        </StyledMiddleColumnGrid>
                    </GridCell>
                }
            </StyledModalGrid>
        )
    }
}


const StyledMiddleColumnGrid = css(Grid)({
    justifyContent: 'center',
    paddingBottom: '20px',
    ' .image-wrapper': {
        textAlign: 'center',
        verticalAlign: 'middle',
        alignSelf: 'center',
    },
    ' .muk-gridcell': {
        alignSelf: 'center',
    }
})

const StyledModalGrid = css(Grid)({
    width: '100%',
    height: '100%',
    ' .muk-gridcell': {
        alignSelf: 'center',
        textAlign: 'center',
    },
    ' .title': {
        padding: '20px 0',
        color: '#3a3a3a',
        fontSize: '20px',
    },
    ' .alt-images': {
        paddingLeft: '10px',
    },
    ' .image-div': {
        alignContent: 'center',
        ' img': {
            border: '1px solid #ccc',
            boxShadow: "0px 2px 5px 0 rgba(0,0,0,0.35)",
        },
    },
    ' .small-div': {
        height: 'calc(100vh - 200px)',
        ' img': {
            maxHeight: 'calc(100vh - 210px)',
        },
    },
    ' .big-div': {
        height: 'calc(100vh - 300px)',
        ' img': {
            maxHeight: 'calc(100vh - 310px)',
        },
    },
    ' .item-id': {
        color: '#3a3a3a',
        fontSize: '20px',
    },
    ' .visible': {
        display: 'inherit',
    },
    ' .hidden': {
        display: 'none',
    },
})

function mapStateToProps(state) {
    return {
        previewsForm: selectFormValues(state, 'previewsForm'),
        settingsForm: selectFormValues(state, 'settingsForm'),
        templates: state.templates,
        gradients: state.gradients,
    }
}

export default connect(mapStateToProps, {
    setGradientsToStore,
})(ImagePreviewModalWrapper)